import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import taskDetailJson from '../../demo/fixtures/task-detail.json'
import copyJson from '../../demo/fixtures/w3/copy.json'
import { selectOptimizations, useAttachedCreatives, useCampaign, useCreativeLibrary } from '../../demo/selectors'
import { useDemoStore } from '../../demo/store'
import { Badge, Button, Card, CardHead, Icon, Toast, ToastHost } from '../../ui'
import { AiGenerateDialog } from '../creatives/AiGenerateDialog'
import { CreativePickerDialog } from '../creatives/CreativePickerDialog'
import { uploadCreativeFile } from '../creatives/copy'
import styles from './CreativeSection.module.css'

interface CopyFixture { adjusted: string; aiPicked: string }
const autopilotCopy = copyJson as unknown as CopyFixture

export function CreativeSection({ campaignId }: { campaignId: string }) {
  const campaign = useCampaign(campaignId)
  const productId = campaign?.productId ?? ''
  const filter = useMemo(() => ({ productId }), [productId])
  const library = useCreativeLibrary(filter)
  const attachedSnapshot = useAttachedCreatives(campaignId)
  const attachedIds = attachedSnapshot.map((creative) => creative.id)
  const selectedIds = useDemoStore((state) => state.selectedByCampaign[campaignId] ?? [])
  const optimizationState = useDemoStore((state) => state.optimizations)
  const toggleSelected = useDemoStore((state) => state.toggleSelected)
  const detachCreatives = useDemoStore((state) => state.detachCreatives)
  const attachCreatives = useDemoStore((state) => state.attachCreatives)
  const location = useLocation()
  const navigate = useNavigate()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [aiOpen, setAiOpen] = useState(false)
  const [toast, setToast] = useState<string | null>(() => (location.state as { creativeToast?: string } | null)?.creativeToast ?? null)
  const inputRef = useRef<HTMLInputElement>(null)
  const sectionRef = useRef<HTMLDivElement>(null)
  const byId = new Map(library.map((creative) => [creative.id, creative]))
  const attached = attachedIds.flatMap((id) => byId.get(id) ?? [])
  const optimizations = useMemo(() => { void optimizationState; return selectOptimizations(campaignId) }, [campaignId, optimizationState])
  const adjustment = optimizations.find((item) => !item.undone && (item.type === 'creative' || item.type === 'compliance')) ?? null
  const pickedAt = new Map<string, string>()
  for (const item of optimizations) {
    if (!item.undone && item.payload?.kind === 'creative' && !pickedAt.has(item.payload.toCreativeId)) pickedAt.set(item.payload.toCreativeId, item.at)
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('focus') !== 'creatives') return
    params.delete('focus')
    window.requestAnimationFrame(() => sectionRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }))
    void navigate(`${location.pathname}${params.size ? `?${params}` : ''}`, { replace: true, state: null })
  }, [location.pathname, location.search, navigate])

  if (!campaign) return null
  const toggle = (id: string) => {
    const result = toggleSelected(campaignId, id)
    if (!result.ok) setToast(result.reason ?? '暂时不能更改素材')
  }
  const upload = async (file?: File) => {
    if (!file) return
    const result = await uploadCreativeFile(file, productId)
    if (result.id) { attachCreatives(campaignId, [result.id]); setToast(`已上传「${file.name}」并加进本广告`) }
    else if (result.error) setToast(result.error)
  }

  return (
    <div ref={sectionRef} data-scope="inc-detail-creatives">
      <Card data-scope="detail-creatives">
        <CardHead title={<>{taskDetailJson.sectionTitles.creatives}{adjustment ? <Badge tone="green" className={styles.adjustedBadge} data-scope="ap-adjusted-badge" title={`${adjustment.at} · ${adjustment.why}`}>{autopilotCopy.adjusted}</Badge> : null}</>} subtitle={`已选用 ${selectedIds.length} 条 · 对应 ${selectedIds.length} 条投放广告`} action={<div className={styles.actions}><Button variant="secondary" onClick={() => setPickerOpen(true)}><Icon name="vault" size={17} />从素材库添加</Button><Button variant="secondary" onClick={() => setAiOpen(true)}><Icon name="sparkle" size={17} />AI 生成</Button></div>} />
        <div className={styles.pad}>
          <div className={styles.grid}>
            {attached.map((creative) => {
              const selected = selectedIds.includes(creative.id)
              const image = creative.dataUrl || (creative.cover.startsWith('/') ? creative.cover : `/${creative.cover}`)
              return <div key={creative.id} role="button" tabIndex={0} className={`${styles.card} ${selected ? styles.selected : ''}`} onClick={() => toggle(creative.id)} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') toggle(creative.id) }}>
                <div className={styles.figure}><img src={image} alt={creative.name} />
                  {pickedAt.has(creative.id) ? <span className={styles.aiMark} data-scope="ap-creative-mark" title={autopilotCopy.aiPicked.replace('{at}', pickedAt.get(creative.id) ?? '')}><Icon name="sparkle" size={13} strokeWidth={2.2} /></span> : null}
                  {creative.kind === 'video' ? <span className={styles.media}><Icon name="play" size={12} />{creative.duration}</span> : <span className={styles.media}>图片</span>}
                  {creative.compliance ? <span className={`${styles.flag} ${creative.compliance.level === 'violation' ? styles.violation : styles.risk}`} title={creative.compliance.reason}><Icon name="alert" size={12} />{creative.compliance.level === 'violation' ? '违规' : '风险'}</span> : null}
                  {selected ? <span className={styles.tick}><Icon name="check" size={13} strokeWidth={3} /></span> : <button type="button" aria-label="从本广告移除" className={styles.remove} onClick={(event) => { event.stopPropagation(); detachCreatives(campaignId, [creative.id]); setToast('已从本广告移除，素材还在库里') }}><Icon name="x" size={14} /></button>}
                </div>
                <div className={styles.body}><p>{creative.name}</p><Badge tone={selected ? 'green' : 'gray'}>{selected ? taskDetailJson.creativeStates.selected : taskDetailJson.creativeStates.generated}</Badge></div>
              </div>
            })}
            <button type="button" className={styles.upload} onClick={() => inputRef.current?.click()}><Icon name="upload" size={26} strokeWidth={1.8} /><span>{taskDetailJson.uploadLabel}</span></button>
          </div>
          <input ref={inputRef} hidden type="file" accept="image/*,video/*" onChange={(event) => { void upload(event.target.files?.[0]); event.currentTarget.value = '' }} />
        </div>
      </Card>
      {pickerOpen ? <CreativePickerDialog open campaignId={campaignId} productId={productId} attachedIds={attachedIds} mode="multi" onClose={() => setPickerOpen(false)} onToast={setToast} /> : null}
      {aiOpen ? <AiGenerateDialog open initialProductId={productId} campaignId={campaignId} onClose={() => setAiOpen(false)} onToast={setToast} /> : null}
      {toast ? <ToastHost><Toast onDismiss={() => setToast(null)}>{toast}</Toast></ToastHost> : null}
    </div>
  )
}
